import { useState, useEffect } from 'react'
import { BarChart3, TrendingUp, DollarSign, Zap, Calendar, Users, Brain, FileText, Activity, Database, Shield, FileCheck, MessageSquare, GitBranch, Settings } from 'lucide-react'

interface PlatformKeysAnalytics {
  dailyUsage: Array<{
    date: string
    requests: number
    tokensInput: number
    tokensOutput: number
    totalTokens: number
    estimatedCost: number
  }>
  monthlyUsage: Array<{
    month: string
    requests: number
    tokensInput: number
    tokensOutput: number
    totalTokens: number
    estimatedCost: number
  }>
  byTenant: Array<{
    tenantId: string
    requests: number
    tokensInput: number
    tokensOutput: number
    totalTokens: number
    estimatedCost: number
  }>
  byKey: Array<{
    secretKey: string
    requests: number
    tokensInput: number
    tokensOutput: number
    totalTokens: number
    estimatedCost: number
  }>
  totalRequests: number
  totalTokensInput: number
  totalTokensOutput: number
  totalCost: number
}

interface GeneralAIUsage {
  totalCost: number
  totalInputTokens: number
  totalOutputTokens: number
  totalTokens: number
  totalRequests: number
  currentMonth: string
  topTenants: Array<{
    tenantId: string
    tenantName: string
    requests: number
    cost: number
    tokens: number
  }>
}

interface FeatureUsageStats {
  byFeature: Array<{
    feature: string
    category: string
    totalEvents: number
    uniqueTenants: number
    tenants: Array<{
      tenantId: string
      tenantName?: string
      count: number
      lastUsed: string
    }>
    dailyTrend: Array<{
      date: string
      count: number
    }>
  }>
  byCategory: Array<{
    category: string
    totalEvents: number
    uniqueFeatures: number
    uniqueTenants: number
    topFeatures: Array<{
      feature: string
      count: number
    }>
  }>
  byTenant: Array<{
    tenantId: string
    tenantName?: string
    totalEvents: number
    uniqueFeatures: number
    topFeatures: Array<{
      feature: string
      count: number
    }>
    lastActivity: string
  }>
  totalEvents: number
  uniqueTenants: number
  uniqueFeatures: number
  dateRange: {
    start: string
    end: string
  }
}

const PlatformAnalytics = () => {
  const [platformKeysAnalytics, setPlatformKeysAnalytics] = useState<PlatformKeysAnalytics | null>(null)
  const [generalAIUsage, setGeneralAIUsage] = useState<GeneralAIUsage | null>(null)
  const [featureUsage, setFeatureUsage] = useState<FeatureUsageStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')
  const [activeSection, setActiveSection] = useState<'feature-usage' | 'platform-keys' | 'general-ai'>('feature-usage')

  useEffect(() => {
    const controller = new AbortController()

    const loadAnalytics = async () => {
      setLoading(true)
      setError(null)

      try {
        // Load Platform Keys analytics
        let startDate: string | undefined
        if (timeRange !== 'all') {
          const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
          const date = new Date()
          date.setDate(date.getDate() - days)
          startDate = date.toISOString()
        }

        const params = new URLSearchParams()
        if (startDate) params.set('startDate', startDate)

        const [platformKeysRes, dashboardRes, featureUsageRes] = await Promise.all([
          fetch(`/api/admin/platform-keys/analytics?${params}`, {
            credentials: 'include',
            signal: controller.signal
          }),
          fetch('/api/platform-admin/dashboard', {
            credentials: 'include',
            signal: controller.signal
          }),
          fetch(`/api/platform-admin/feature-usage?${params}`, {
            credentials: 'include',
            signal: controller.signal
          })
        ])

        if (platformKeysRes.ok) {
          const data = await platformKeysRes.json()
          setPlatformKeysAnalytics(data)
        }

        if (dashboardRes.ok) {
          const dashboard = await dashboardRes.json()
          setGeneralAIUsage(dashboard.metrics.aiSpending)
        }

        if (featureUsageRes.ok) {
          const data = await featureUsageRes.json()
          console.log('[PlatformAnalytics] Feature usage data:', data)
          setFeatureUsage(data)
        } else {
          console.error('[PlatformAnalytics] Failed to fetch feature usage:', featureUsageRes.status, await featureUsageRes.text())
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return // Ignore cancelled requests
        }
        console.error('Error loading analytics:', err)
        setError(err instanceof Error ? err.message : 'Failed to load analytics')
      } finally {
        setLoading(false)
      }
    }

    loadAnalytics()

    return () => controller.abort()
  }, [timeRange])

  const loadAnalytics = async () => {
    setLoading(true)
    setError(null)

    try {
      // Load Platform Keys analytics
      let startDate: string | undefined
      if (timeRange !== 'all') {
        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
        const date = new Date()
        date.setDate(date.getDate() - days)
        startDate = date.toISOString()
      }

      const params = new URLSearchParams()
      if (startDate) params.set('startDate', startDate)

      const [platformKeysRes, dashboardRes, featureUsageRes] = await Promise.all([
        fetch(`/api/admin/platform-keys/analytics?${params}`, {
          credentials: 'include'
        }),
        fetch('/api/platform-admin/dashboard', {
          credentials: 'include'
        }),
        fetch(`/api/platform-admin/feature-usage?${params}`, {
          credentials: 'include'
        })
      ])

      if (platformKeysRes.ok) {
        const data = await platformKeysRes.json()
        setPlatformKeysAnalytics(data)
      }

      if (dashboardRes.ok) {
        const dashboard = await dashboardRes.json()
        setGeneralAIUsage(dashboard.metrics.aiSpending)
      }

      if (featureUsageRes.ok) {
        const data = await featureUsageRes.json()
        console.log('[PlatformAnalytics] Feature usage data:', data)
        setFeatureUsage(data)
      } else {
        console.error('[PlatformAnalytics] Failed to fetch feature usage:', featureUsageRes.status, await featureUsageRes.text())
      }
    } catch (err) {
      console.error('Error loading analytics:', err)
      setError(err instanceof Error ? err.message : 'Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-gray-600">Loading analytics...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    )
  }

  const platformKeysMaxDailyCost = platformKeysAnalytics
    ? Math.max(...(platformKeysAnalytics.dailyUsage || []).map(d => d.estimatedCost), 0.01)
    : 0.01

  return (
    <div className="space-y-6">
      {/* Header with Section Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <BarChart3 className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Platform Analytics</h2>
            <p className="text-sm text-gray-600">Feature usage, engagement, and cost tracking across all systems</p>
          </div>
        </div>

        <div className="flex gap-2">
          {(['7d', '30d', '90d', 'all'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {range === 'all' ? 'All Time' : range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Section Toggle */}
      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
        <button
          onClick={() => setActiveSection('feature-usage')}
          className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 font-medium transition border-b-2 whitespace-nowrap touch-manipulation ${
            activeSection === 'feature-usage'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Activity className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm sm:text-base">Feature Usage</span>
          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
            All Features
          </span>
        </button>
        <button
          onClick={() => setActiveSection('general-ai')}
          className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 font-medium transition border-b-2 whitespace-nowrap touch-manipulation ${
            activeSection === 'general-ai'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Brain className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm sm:text-base">Platform AI Usage</span>
          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
            Shared Key
          </span>
        </button>
        <button
          onClick={() => setActiveSection('platform-keys')}
          className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 font-medium transition border-b-2 whitespace-nowrap touch-manipulation ${
            activeSection === 'platform-keys'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm sm:text-base">Tenant-Specific Keys</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            Custom Keys
          </span>
        </button>
      </div>

      {/* Feature Usage Section */}
      {activeSection === 'feature-usage' && !featureUsage && (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No feature usage data available</h3>
          <p className="text-gray-600 mb-4">
            Feature usage tracking is enabled. Data will appear here once platform features are used.
          </p>
          <p className="text-sm text-gray-500">
            Try uploading a document, creating an access request, or using the knowledge base to see tracking in action.
          </p>
        </div>
      )}

      {activeSection === 'feature-usage' && featureUsage && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-600">Total Events</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {featureUsage.totalEvents.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {featureUsage.dateRange.start} to {featureUsage.dateRange.end}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-gray-600">Active Tenants</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {featureUsage.uniqueTenants}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Using platform features
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Features Used</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {featureUsage.uniqueFeatures}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Unique features accessed
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-600">Avg per Tenant</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {featureUsage.uniqueTenants > 0 ? (featureUsage.totalEvents / featureUsage.uniqueTenants).toFixed(0) : '0'}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Events per active tenant
              </div>
            </div>
          </div>

          {/* Feature Usage by Category */}
          {featureUsage.byCategory.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                Usage by Feature Category
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {featureUsage.byCategory.map((cat) => (
                  <div key={cat.category} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {cat.category === 'trust-center' && <Shield className="w-4 h-4 text-blue-600" />}
                        {cat.category === 'documents' && <FileText className="w-4 h-4 text-green-600" />}
                        {cat.category === 'knowledge-base' && <Brain className="w-4 h-4 text-purple-600" />}
                        {cat.category === 'tprm' && <FileCheck className="w-4 h-4 text-orange-600" />}
                        {cat.category === 'privacy' && <Shield className="w-4 h-4 text-red-600" />}
                        {cat.category === 'changelog' && <MessageSquare className="w-4 h-4 text-indigo-600" />}
                        {cat.category === 'deployments' && <GitBranch className="w-4 h-4 text-gray-600" />}
                        {cat.category === 'admin' && <Settings className="w-4 h-4 text-gray-600" />}
                        <span className="font-medium text-gray-900 capitalize">
                          {cat.category.replace(/-/g, ' ')}
                        </span>
                      </div>
                      <span className="text-lg font-bold text-blue-600">
                        {cat.totalEvents.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                      <span>{cat.uniqueFeatures} features</span>
                      <span>{cat.uniqueTenants} tenants</span>
                    </div>
                    {cat.topFeatures.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="text-xs font-medium text-gray-500 mb-2">Top Features:</div>
                        <div className="space-y-1">
                          {cat.topFeatures.slice(0, 3).map((feat) => (
                            <div key={feat.feature} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 truncate flex-1">
                                {feat.feature.split('.')[1]?.replace(/-/g, ' ') || feat.feature}
                              </span>
                              <span className="text-gray-900 font-medium ml-2">{feat.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Features Overall */}
          {featureUsage.byFeature.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Most Popular Features
              </h3>
              <div className="space-y-2">
                {featureUsage.byFeature.slice(0, 15).map((feat, index) => {
                  const maxEvents = featureUsage.byFeature[0]?.totalEvents || 1
                  return (
                    <div key={feat.feature} className="flex items-center gap-3">
                      <div className="text-xs text-gray-500 w-6 text-right">{index + 1}</div>
                      <div className="text-sm text-gray-700 w-48 truncate" title={feat.feature}>
                        {feat.feature}
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-purple-500 to-blue-500 h-full rounded-full flex items-center justify-end pr-2"
                          style={{
                            width: `${(feat.totalEvents / maxEvents) * 100}%`,
                            minWidth: feat.totalEvents > 0 ? '40px' : '0px'
                          }}
                        >
                          {feat.totalEvents > 0 && (
                            <span className="text-xs font-medium text-white">
                              {feat.totalEvents}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 w-20 text-right">
                        {feat.uniqueTenants} {feat.uniqueTenants === 1 ? 'tenant' : 'tenants'}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Most Active Tenants */}
          {featureUsage.byTenant.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                Most Active Tenants
              </h3>
              <div className="space-y-3">
                {featureUsage.byTenant.slice(0, 10).map((tenant) => (
                  <div key={tenant.tenantId} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-gray-900 truncate flex-1" title={tenant.tenantName || tenant.tenantId}>
                        {tenant.tenantName || tenant.tenantId}
                      </div>
                      <div className="text-lg font-bold text-orange-600">
                        {tenant.totalEvents.toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                      <span>{tenant.uniqueFeatures} features used</span>
                      <span>Last: {new Date(tenant.lastActivity).toLocaleDateString()}</span>
                    </div>
                    {tenant.topFeatures.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {tenant.topFeatures.slice(0, 5).map((feat) => (
                          <span
                            key={feat.feature}
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                            title={feat.feature}
                          >
                            {feat.feature.split('.')[1]?.substring(0, 15) || feat.feature.substring(0, 15)}
                            <span className="ml-1 font-medium">({feat.count})</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Usage Trends Over Time */}
          {featureUsage.byFeature.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Usage Trends Over Time
              </h3>
              <div className="space-y-6">
                {featureUsage.byFeature.slice(0, 8).map((feature) => {
                  if (!feature.dailyTrend || feature.dailyTrend.length === 0) return null

                  const maxCount = Math.max(...feature.dailyTrend.map(d => d.count), 1)

                  return (
                    <div key={feature.feature} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="font-medium text-gray-900">
                            {feature.feature.split('.')[1]?.replace(/-/g, ' ') || feature.feature}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">
                            {feature.category.replace(/-/g, ' ')} • {feature.totalEvents} total events
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {feature.uniqueTenants} tenant{feature.uniqueTenants !== 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Simple bar chart */}
                      <div className="flex items-end gap-1 h-20">
                        {feature.dailyTrend.map((day, idx) => {
                          const heightPercent = (day.count / maxCount) * 100
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center justify-end group relative">
                              <div
                                className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all hover:from-blue-600 hover:to-blue-500"
                                style={{ height: `${heightPercent}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                              />
                              {/* Tooltip on hover */}
                              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {day.count} events
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      {/* Date labels */}
                      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <span>{feature.dailyTrend[0]?.date ? new Date(feature.dailyTrend[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                        <span>{feature.dailyTrend[feature.dailyTrend.length - 1]?.date ? new Date(feature.dailyTrend[feature.dailyTrend.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {featureUsage.totalEvents === 0 && (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No feature usage data yet</h3>
              <p className="text-gray-600">
                Feature usage tracking will appear here once tenants start using platform features
              </p>
            </div>
          )}
        </div>
      )}

      {/* General AI Usage Section */}
      {activeSection === 'general-ai' && generalAIUsage && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Total Requests</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {generalAIUsage.totalRequests.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {generalAIUsage.currentMonth || 'Current month'}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-600">Total Tokens</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {(generalAIUsage.totalTokens / 1000000).toFixed(2)}M
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {(generalAIUsage.totalInputTokens / 1000000).toFixed(2)}M in / {(generalAIUsage.totalOutputTokens / 1000000).toFixed(2)}M out
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-600">Estimated Cost</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                ${generalAIUsage.totalCost.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                KB queries, doc scanning
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-gray-600">Active Tenants</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {generalAIUsage.topTenants.length}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                With AI usage
              </div>
            </div>
          </div>

          {/* Top Tenants by Cost */}
          {generalAIUsage.topTenants.length > 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                Top Tenants by AI Spending
              </h3>
              <div className="space-y-2">
                {generalAIUsage.topTenants.map((tenant) => (
                  <div key={tenant.tenantId} className="flex items-center gap-3">
                    <div className="text-sm text-gray-700 w-48 truncate" title={tenant.tenantName}>
                      {tenant.tenantName}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full flex items-center justify-end pr-2"
                        style={{
                          width: `${generalAIUsage.totalCost > 0 ? (tenant.cost / generalAIUsage.totalCost) * 100 : 0}%`,
                          minWidth: tenant.cost > 0 ? '40px' : '0px'
                        }}
                      >
                        {tenant.cost > 0 && (
                          <span className="text-xs font-medium text-white">
                            ${tenant.cost.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 w-24 text-right">
                      {tenant.requests} req
                    </div>
                    <div className="text-xs text-gray-500 w-20 text-right">
                      {(tenant.tokens / 1000000).toFixed(2)}M
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
              <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No general AI usage yet</h3>
              <p className="text-gray-600">
                Analytics will appear here once tenants start using AI features like KB queries and document scanning
              </p>
            </div>
          )}
        </div>
      )}

      {/* Platform Keys Section */}
      {activeSection === 'platform-keys' && platformKeysAnalytics && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-600">Total Requests</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {platformKeysAnalytics.totalRequests.toLocaleString()}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-600">Total Tokens</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {((platformKeysAnalytics.totalTokensInput + platformKeysAnalytics.totalTokensOutput) / 1000000).toFixed(2)}M
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {(platformKeysAnalytics.totalTokensInput / 1000000).toFixed(2)}M in / {(platformKeysAnalytics.totalTokensOutput / 1000000).toFixed(2)}M out
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-600">Estimated Cost</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                ${platformKeysAnalytics.totalCost.toFixed(2)}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-gray-600">Active Tenants</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {platformKeysAnalytics.byTenant.length}
              </div>
            </div>
          </div>

          {/* Daily Usage Chart */}
          {platformKeysAnalytics.dailyUsage.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Daily Cost Trend
              </h3>
              <div className="space-y-2">
                {platformKeysAnalytics.dailyUsage.slice(-30).map((day) => (
                  <div key={day.date} className="flex items-center gap-3">
                    <div className="text-xs text-gray-600 w-20">{day.date}</div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-full rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${(day.estimatedCost / platformKeysMaxDailyCost) * 100}%` }}
                      >
                        {day.estimatedCost > 0 && (
                          <span className="text-xs font-medium text-white">
                            ${day.estimatedCost.toFixed(3)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 w-16 text-right">
                      {day.totalTokens > 0 ? `${(day.totalTokens / 1000).toFixed(0)}K` : '-'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly Summary */}
          {platformKeysAnalytics.monthlyUsage.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Summary</h3>
              <div className="space-y-3">
                {platformKeysAnalytics.monthlyUsage.map((month) => (
                  <div key={month.month} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900">{month.month}</div>
                      <div className="text-sm text-gray-600">
                        {month.requests.toLocaleString()} requests • {(month.totalTokens / 1000000).toFixed(2)}M tokens
                      </div>
                    </div>
                    <div className="text-lg font-bold text-purple-600">
                      ${month.estimatedCost.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Usage by Tenant */}
          {platformKeysAnalytics.byTenant.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-600" />
                Usage by Tenant
              </h3>
              <div className="space-y-2">
                {platformKeysAnalytics.byTenant.slice(0, 10).map((tenant) => (
                  <div key={tenant.tenantId} className="flex items-center gap-3">
                    <div className="text-sm text-gray-700 w-40 truncate" title={tenant.tenantId}>
                      {tenant.tenantId === '*' ? 'All Tenants (Wildcard)' : tenant.tenantId}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${(tenant.estimatedCost / platformKeysAnalytics.totalCost) * 100}%` }}
                      >
                        {tenant.estimatedCost > 0 && (
                          <span className="text-xs font-medium text-white">
                            ${tenant.estimatedCost.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 w-20 text-right">
                      {tenant.requests} req
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Usage by API Key */}
          {platformKeysAnalytics.byKey.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage by API Key</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {platformKeysAnalytics.byKey.map((key) => (
                  <div key={key.secretKey} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-gray-900">
                        {key.secretKey === 'claudeApiKey' ? 'Claude API' : 'OpenAI API'}
                      </div>
                      <div className="text-lg font-bold text-blue-600">
                        ${key.estimatedCost.toFixed(2)}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {key.requests.toLocaleString()} requests
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {(key.totalTokens / 1000000).toFixed(2)}M tokens ({(key.tokensInput / 1000000).toFixed(2)}M in / {(key.tokensOutput / 1000000).toFixed(2)}M out)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {platformKeysAnalytics.totalRequests === 0 && (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tenant-specific keys configured</h3>
              <p className="text-gray-600">
                This section tracks usage when tenants configure their own API keys. Currently all tenants use the shared platform Anthropic key (see "General AI Usage" tab).
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PlatformAnalytics
